"use client"

import { isAxiosError } from "axios"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { FlaskConical, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { addManualWatchlist } from "@/lib/api/watchlist"

interface AddToBacktestButtonProps {
  stockCode: string
  inWatchlist: boolean
  className?: string
}

export function AddToBacktestButton({ stockCode, inWatchlist, className }: AddToBacktestButtonProps) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => addManualWatchlist(stockCode),
    onSuccess: () => {
      toast.success("관심종목에 추가되었습니다")
      queryClient.invalidateQueries({ queryKey: ["watchlist", "codes"] })
      queryClient.invalidateQueries({ queryKey: ["backtest", "watchlist"] })
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.info("이미 추가된 종목입니다")
        queryClient.invalidateQueries({ queryKey: ["watchlist", "codes"] })
        return
      }
      toast.error("관심종목 추가에 실패했습니다")
    },
  })

  const disabled = inWatchlist || mutation.isPending

  return (
    <button
      type="button"
      disabled={disabled}
      title={inWatchlist ? "이미 관심종목에 추가됨" : "관심종목에 추가"}
      onClick={(e) => {
        e.stopPropagation()
        if (!inWatchlist) mutation.mutate()
      }}
      className={cn(
        "inline-flex size-7 shrink-0 items-center justify-center rounded-md transition-colors",
        inWatchlist
          ? "cursor-default text-emerald-500/70"
          : "text-zinc-400 hover:bg-zinc-800 hover:text-emerald-400",
        className
      )}
    >
      {mutation.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <FlaskConical className="size-4" />
      )}
    </button>
  )
}
